/**
 * Guided bidding - Paul's hands and commentary, VERBATIM (never reword; see CLAUDE.md).
 * Source of truth for review: docs/guided-bidding/responding-with-majors.md
 *
 * Categories -> sets -> hands -> steps. A category with no sets shows as "Coming soon".
 *
 * Bids are shorthand: "3C", "3N" (NT), "P" (pass), "X", "XX". Text shorthand (3C, 3NT, 4S)
 * renders as coloured suit symbols.
 *
 * Auction: dealer North, seats W N E S. `start` is the calls before South's first turn
 * ("-" = seat not yet bid, shown blank). Each later step's `continuation` is the calls
 * made after South's previous bid (W, N, E) up to South's next turn.
 *
 * Commentary markup (standing rule: link every bid the commentary refers to):
 *   - a bare bid in the text (3D, 3NT) that is already in the auction links automatically
 *     to its latest occurrence;
 *   - [[i|phrase]] links a phrase to auction call index i (0 = West's first seat, 1 = North's 1NT ...);
 *   - {{art|text}} opens the hand's article.
 *
 * Videos: `video` on a step (or on a hand) takes a YouTube URL. Premium-only (standing rule
 * 25 Sep 2026) - basic members see an upgrade note, everyone else a membership note.
 *
 * Ids (`hand.id`, `step.id`) key each member's results in Firestore - never renumber them;
 * add new hands with new ids.
 */

export const ARTICLES = {
  puppet: {
    title: "Puppet Stayman Convention - All You Need to Know",
    path: "/learn/bidding/puppet-stayman-check-5-card",
  },
  transfers: {
    title: "System Over 1NT: Transfer Responses and Conventions",
    path: "/learn/bidding/system-over-1nt-conventions",
  },
  stayman: {
    title: "The Stayman Convention in Bridge: How and When to Use It",
    path: "/learn/bidding/stayman-convention",
  },
  smolen: {
    title: "Smolen Convention: Show 5-4 Majors After Stayman",
    path: "/learn/bidding/smolen-convention-show-5-4",
  },
  majorFit: {
    title: "Find a Major Fit After 1NT: Stayman, Smolen, Puppet, Texas",
    path: "/learn/bidding/find-major-fit-after-1nt",
  },
  garbage: {
    title: "Garbage stayman: Stayman with weak hands",
    path: "/learn/bidding/weak-stayman-know-helps",
  },
  open1c: {
    title: "Bidding fundamentals #1 - Learn and Checklist - Open 1♣",
    path: "/learn/bidding/bidding-fundamentals-open-1c",
  },
  forcing: {
    title: "Is that bid forcing? Responder's first bid.",
    path: "/learn/bidding/responders-first-bid-forcing",
  },
  raising: {
    title: "Raising partner. Simply supporting is best.",
    path: "/learn/bidding/kiss-3-support-partner-priority",
  },
  ltc: {
    title: "Losing Trick Count for Bidding: Judge Contracts Better",
    path: "/learn/bidding/loser-count-bidding-judge-contracts",
  },
  supportDouble: {
    title: "Support Doubles in Bridge: How and When to Use Them",
    path: "/learn/bidding/support-doubles",
  },
  art2nt: {
    title: "Bidding fundamentals #6 - Learn and Checklist - Artificial 2NT rebid",
    path: "/learn/bidding/bidding-fundamentals-artificial-2nt-rebid",
  },
  shortage1: {
    title: "Points opposite Shortage: Part 1 - Fundamentals",
    path: "/learn/bidding/points-opposite-shortage-1",
  },
  fourthSuit: {
    title: "Fourth Suit Forcing: How and When to Use It",
    path: "/learn/bidding/fourth-suit-forcing-checkback-core",
  },
  twoOverOne: {
    title: "2 over 1: Game forcing",
    path: "/learn/bidding/2-over-1-game-forcing",
  },
  // Part 2 of the 2 over 1 series (built 4 Nov 2026; article not live until Paul says publish).
  twoOverOneRebids: {
    title: "2 over 1, rebids - simplified",
    path: "/learn/bidding/2-over-1-rebids-simplified",
  },
};

const OPEN_1NT = ["-", "1N", "P"];
const SOUTH_A1084 = { S: "A 10 8 4", H: "7 2", D: "K Q 9 5", C: "J 10 3" };

const RESPONDING_WITH_MAJORS = {
  id: "responding-with-majors",
  title: "Responding with majors",
  // Recommended reading for the whole set (Paul, 26 Sep 2026).
  reading: "majorFit",
  hands: [
    {
      id: "rwm-1",
      video: "https://youtu.be/HQy1xMONq2E", // "Hand 1" on the channel, uploaded 26 Sep 2026
      article: "puppet",
      south: { S: "K 10 7 4", H: "A 9 3", D: "5 2", C: "K 8 6 3" },
      start: OPEN_1NT,
      final: "4S by North",
      steps: [
        {
          id: "b1",
          answer: "3C",
          comment:
            "The preferred bid is 3C, puppet stayman on a hand like this. If partner bids a 5 card major, great just raise it to game. Otherwise, if not, tell partner about your 4 card spade suit, and either play in 4S or 3NT.",
          rule: "with 4 - 3 in the majors, use puppet Stayman - because we keep open the possibility of the 5-3 fit as well.",
        },
        {
          id: "b2",
          answer: "3H",
          continuation: ["P", "3D", "P"],
          comment:
            "With puppet stayman, the best way to play it is that [[5|partners 3D]] just denies a 5 card major, it may or may not have a 4 card major. We now just tell partner about our 4 card spade suit by bidding the suit we DON'T have.",
        },
        {
          id: "b3",
          answer: "4S",
          continuation: ["P", "3S", "P"],
          comment:
            "Just simply raise this to game, [[9|partner has shown 4 card spades]], and is leaving the bidding low in case we were interested in slam.",
        },
      ],
    },
    {
      id: "rwm-2",
      video: "https://youtu.be/YM1IojBLXyk", // "Hand 2" on the channel, uploaded 26 Sep 2026
      article: "transfers",
      south: { S: "K Q 10 8 4", H: "5 3", D: "A 10 3", C: "Q 8 4" },
      start: OPEN_1NT,
      steps: [
        { id: "b1", answer: "2H", comment: "Simply transfer to spades by bidding 2H, this shows 5 card spades." },
        {
          id: "b2",
          answer: "3N",
          continuation: ["P", "2S", "P"],
          comment: "Bid 3NT here offering partner a choice of games - either 3NT or 4S.",
        },
      ],
    },
    {
      id: "rwm-3",
      video: "https://youtu.be/4w3qS-ajjHw", // "Hand 3" on the channel, uploaded 26 Sep 2026
      article: "stayman",
      south: SOUTH_A1084,
      start: OPEN_1NT,
      intro: "There are two ways to bid this hand, both are fine - Lets first look at using simple stayman.",
      steps: [
        {
          id: "b1",
          answer: "2C",
          comment:
            "With a balanced hand and enough points for game, we should basically be interested in either 4 of a major or 3NT. We would never bother looking for a minor fit, it will always be inferior to 3NT with two balanced hands opposite each other.",
        },
        {
          id: "b2",
          answer: "3N",
          continuation: ["P", "2H", "P"],
          comment:
            "Here we just bid 3NT, we do not bid 2S. 3NT shows 4 card spades - why is that? Well, if you didn't have 4 card spades, you wouldn't have [[3|bid stayman]] at all - the fact that you did, means you have at least one 4 card major.",
        },
      ],
    },
    {
      id: "rwm-4",
      video: "https://youtu.be/Po2iQpB-MdM", // "Hand 4" on the channel, uploaded 26 Sep 2026
      article: "puppet",
      south: SOUTH_A1084,
      start: OPEN_1NT,
      final: "3NT by North",
      intro: "This is my slight preference",
      rule: "With one 4 card major, it is preferable to use puppet stayman instead of regular stayman, lets look at how to do it.",
      steps: [
        { id: "b1", answer: "3C", comment: "Puppet stayman is triggered by bidding 3C" },
        {
          id: "b2",
          answer: "3H",
          continuation: ["P", "3D", "P"],
          comment:
            "after [[5|partner denies a 5 card major by bidding 3D]], we simply bid the major we don't have. Showing 4 spades",
        },
        {
          id: "b3",
          answer: "P",
          continuation: ["P", "3N", "P"],
          comment:
            "[[9|Partner has denied 4 card spades]]. Partner had the option of bidding 3S or 4S with 4 card spades (its typical to play 4S as showing a bad hand (closing the auction, not giving partner room to investigate slam), and 3S as showing a better hand - keeping the auction lower to allow slam investigation)",
        },
      ],
    },
    {
      id: "rwm-5",
      video: "https://youtu.be/60sUiK9IKDg", // "Hand 5" on the channel, uploaded 26 Sep 2026
      article: "puppet",
      south: { S: "K 10 8 4", H: "Q 10 2", D: "A 9 3", C: "J 8 2" },
      start: OPEN_1NT,
      steps: [
        {
          id: "b1",
          answer: "3N",
          comment:
            "With 4-3-3-3 shape, just bid 3NT! Don't worry about looking for a major fit opposite a 1NT opening.",
        },
      ],
    },
    {
      id: "rwm-6",
      video: "https://youtu.be/oPfCaUig748", // "Hand 6" on the channel, uploaded 26 Sep 2026
      article: "smolen",
      south: { S: "K J 8 3 2", H: "A J 3 2", D: "4", C: "J 10 3" },
      start: OPEN_1NT,
      steps: [
        {
          id: "b1",
          answer: "2C",
          comment:
            'I recommend using "smolen" here, it is very common - almost all top pairs are using it these days',
        },
        {
          id: "b2",
          answer: "3H",
          continuation: ["P", "2D", "P"],
          comment:
            "3H is the correct bid. If partner had just bid a major on the 2 level, things would be easy - you raise it to game. Since [[5|partner denied a 4 card major]], you now show that you have 5 spades by bidding on the 3 level, the major you DON'T have. (check {{art|the article}} for a recap)",
        },
      ],
    },
    {
      id: "rwm-8",
      video: "https://youtu.be/nFay_52JYIQ", // "Hand 8" on the channel, uploaded 26 Sep 2026
      article: "transfers",
      south: { S: "A Q 10 8 4", H: "Q 3", D: "K 10 8 4", C: "J 10" },
      start: OPEN_1NT,
      steps: [
        {
          id: "b1",
          answer: "2H",
          comment:
            "[[3|Simple transfer to spades]], showing 5. With a 5 card major we always want to show it, the only time we don't transfer is if we have both majors, in which case we can use smolen as the previous example showed.",
        },
        {
          id: "b2",
          answer: "3N",
          continuation: ["P", "2S", "P"],
          comment:
            "Just 3NT - we are balanced and suitable for No trump, don't bid the minor for the sake of it. Only bid your minor when you seriously would like to play in it. When your hand is clearly indicating to play in No Trump, don't doubt it - 3NT is a great contract!",
        },
      ],
    },
    {
      id: "rwm-7",
      video: "https://youtu.be/z-_UnbHf8tA", // "Hand 7" on the channel, uploaded 26 Sep 2026
      article: "garbage",
      south: { S: "Q J 10 4", H: "J 8 5 3 2", D: "2", C: "5 4 3" },
      start: OPEN_1NT,
      steps: [
        {
          id: "b1",
          answer: "2C",
          comment:
            "There are two approaches with this, transferring to hearts and passing. Another recommended idea is to play garbage stayman - very popular amongst successful pairs",
        },
        {
          id: "b2",
          answer: "2H",
          continuation: ["P", "2D", "P"],
          comment: "[[7|This]] now shows a weak hand with both majors, and longer hearts.",
          extra: {
            lead: "On a different day, partner might have this hand",
            north: { S: "A K 5 2", H: "7 4", D: "A Q 3", C: "K 10 7 2" },
            after: "2S is a much better contract than 2H. With garbage stayman we end up in 2S rather than 2H.",
          },
        },
      ],
    },
  ],
};

// 1C opening: "Balanced 12-14 hands" (Paul, 28 Sep 2026). His words VERBATIM (typo fixes listed to him:
// wrods, corrolerary, alwways, suport, suprises). South deals and opens 1C, so `start` holds South's own 1C
// (and, in hands 7-8, South's earlier pass) before South's turn.
const OPEN_1C_P_1H = ["-", "-", "-", "1C", "P", "1H"];
const SOUTH_K1084 = { S: "K 10 8 4", H: "5 3", D: "A J 3", C: "K J 9 2" };
const SOUTH_A54 = { S: "A 5 4", H: "K 9 4 2", D: "J 5", C: "K J 10 2" };
const SOUTH_QJ94 = { S: "Q J 9 4", H: "A 2", D: "K 10 3", C: "K J 10 8" };
const BALANCED_12_14 = {
  id: "balanced-12-14",
  // Published 28 Sep 2026 (Paul: "ok put these up").
  title: "Balanced 12-14 hands",
  reading: "open1c",
  // The "before you start" note + rule were removed (Paul, 28 Sep 2026: "get rid of this").
  noteVideo: null, // Paul to supply (28 Sep 2026: "insert video here")
  hands: [
    {
      id: "b1214-1",
      video: "https://youtu.be/ZVE7DeyhrB4", // "1" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "open1c",
      south: SOUTH_K1084,
      start: [...OPEN_1C_P_1H, "2D"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            'Pass, we have the simple 12-14 hand we advertised [[3|when we opened the bidding]]. Partner already knows everything there is nothing more to tell. This is a very important concept to understand, it is the gateway to "bid your hand". The corollary of that is, if you have already bid your hand, its often correct to pass.',
        },
      ],
    },
    {
      id: "b1214-2",
      video: "https://youtu.be/D_S1RHxO25A", // "2" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "forcing",
      south: SOUTH_K1084,
      start: ["-", "-", "-", "1C", "2D", "2H", "P"],
      intro: "A similar hand to the previous example, what do you bid here - have you already bid your hand?",
      steps: [
        {
          id: "b1",
          answer: "2N",
          comment:
            "Bid 2NT: We would love to pass, as we've already shown our hand, yet we can't - [[5|partner's bid]] was forcing ({{art|Is that bid forcing?}}). We therefore need to make a bid that best describes our hand. 2NT is the bid, showing the balanced hand (that partner should have expected most of the time when we [[3|open 1C]], say 80% or more chance of happening). We are showing a [[4|diamond]] stopper of course, and denying 3 card heart support (otherwise we would always support) ({{art:raising|Raising partner. Simply supporting is best.}}).",
        },
      ],
    },
    {
      id: "b1214-3",
      video: "https://youtu.be/i6Vc8CjuUP0", // "3" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "forcing",
      south: { S: "K 10 3", H: "A 9 4 3", D: "5 2", C: "A Q 10 8" },
      start: ["-", "-", "-", "1C", "P", "1S", "P"],
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "Bid 1NT, with a balanced hand do not support partner with 3 cards, always rebid 1NT. As for passing, hopefully you can see that is off the table since [[5|partner's bid]] is forcing.",
        },
      ],
    },
    {
      id: "b1214-4",
      video: "https://youtu.be/tGrQYS6Id3I", // "4" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "raising",
      south: SOUTH_A54,
      start: [...OPEN_1C_P_1H, "2S"],
      steps: [
        {
          id: "b1",
          answer: "3H",
          comment:
            "Bid 3H, with 4 card support for [[5|partner]] make yourself heard, take the push to the 3 level if necessary. This is a rare moment where we push to do a bit extra with our 12-14 balanced hand, supporting partner is key to good bridge.",
        },
      ],
    },
    {
      id: "b1214-5",
      video: "https://youtu.be/n4I05Udc0Ro", // "5" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "raising",
      south: SOUTH_A54,
      start: [...OPEN_1C_P_1H, "3S"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "Pass, draw the line somewhere - it is excessive to commit your side to the 4 level. You could be way too high, this may just be a partscore hand where the 2 level is the last making place for either side. Despite how much it should pain you to not bid your support, you have to draw the line at the three level with a balanced hand.",
        },
      ],
    },
    {
      id: "b1214-6",
      video: "https://youtu.be/_i_vFOokE7M", // "6" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "supportDouble",
      south: { S: "J 4", H: "A 5 3", D: "K 10 3", C: "A 8 4 3 2" },
      start: [...OPEN_1C_P_1H, "1S"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "Double - this is a support double. It shows 3 card support. If you recall I said, with such a hand we would rebid 1NT if there was NO interference. [[6|The interference]] gives us another available bid, double. Many pairs these days, including some of the most successful ones in the world, play support doubles ({{art|Support Doubles in Bridge}}).",
        },
      ],
    },
    {
      id: "b1214-7",
      video: "https://youtu.be/R4RJHOU9Yyk", // "7" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "open1c",
      south: SOUTH_QJ94,
      start: [...OPEN_1C_P_1H, "2D"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "Pass: This is a classic case of passing with 12-14 points, telling partner I have nothing more to say (and of course I do not have heart support).",
        },
        {
          id: "b2",
          answer: "2S",
          continuation: ["P", "X", "P"],
          comment:
            "Bid 2S. In this auction [[7|our pass over 2D]] was excellent - showing that we just have a boring 12-14 hand. [[9|Partner has made a takeout double]] - probably just wanting to compete for the part score, 2S may easily be your best spot. Either way, without trying to predict the future, just simply bid your hand naturally here",
          // Paul: "Do a tree / arrow diagram of how the auction developed". Each box points at its call.
          flow: {
            steps: [
              { ref: 3, text: "You started by opening 1C, likely showing 12-14 points." },
              { ref: 7, text: "You passed, confirming that you just have that minimum expected hand." },
              { ref: 11, text: "You clarified that within that context (having 12-14 balanced), you also have 4 card spades." },
            ],
            after: "You have done an excellent job conveying your hand, no surprises.",
          },
        },
      ],
    },
    {
      id: "b1214-8",
      video: "https://youtu.be/KmSK_mBZs4c", // "8" on the channel, uploaded 28 Sep 2026 - shown at the end of the hand
      article: "open1c",
      south: SOUTH_QJ94,
      start: [...OPEN_1C_P_1H, "2D"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "Pass: This is a very similar situation to the previous hand. We can pass, letting partner know we just have a basic 12-14 balanced hand, without heart support.",
        },
        {
          id: "b2",
          answer: "3N",
          continuation: ["P", "2N", "P"],
          comment:
            "Bid 3NT. [[9|Partner's 2NT]] was natural, invitation. Partner is expecting you to have 12-14 points balanced, [[7|after you passed]]. Nevertheless, partner is asking whether you can accept an invite - you can, you're a maximum.",
        },
      ],
    },
  ],
};

// 1H and 1S opening (Paul, 30 Sep 2026). His words VERBATIM; minor fixes listed to him (THe, th at, "and be able",
// THis, interest -> interesting, "look what" -> "look at what", "recall earlier" -> "recall the earlier", jsut,
// WIth, 1HT -> 1NT in hand 2's auction). South deals and opens; `start` = W/N/E blank, South's opening, W, N, E.
// "RULE: ..." paragraphs are his "ai insert rule" lines. LOCAL ONLY until he has made the videos (see CATEGORIES).
const OPEN_1S_1NT = ["-", "-", "-", "1S", "P", "1N", "P"];
const OPEN_1H_1NT = ["-", "-", "-", "1H", "P", "1N", "P"];
const OPEN_1H_1S = ["-", "-", "-", "1H", "P", "1S", "P"];
const MAJOR_OPENINGS = {
  id: "opener-rebids",
  // Set title: Paul's heading was "1H and 1S opening" (the category); no separate set name given yet.
  title: "1H and 1S opening",
  hands: [
    {
      id: "m1-1",
      video: "https://youtu.be/uAWwAzof7GE", // "1 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "K Q 5 4 2", H: "A J 9 2", D: "10 4", C: "Q 5" },
      start: OPEN_1S_1NT,
      steps: [
        {
          id: "b1",
          answer: "2H",
          comment: "",
          rule: "When you are 5-4 shape, always rebid your second suit, don't pass 1NT.",
        },
      ],
    },
    {
      id: "m1-2",
      video: "https://youtu.be/1tiNfDcwMOA", // "2 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "K Q 10 5 4 2", H: "A J 9 2", D: "2", C: "Q 5" },
      start: OPEN_1S_1NT,
      steps: [
        {
          id: "b1",
          answer: "2H",
          comment: "Even with 6-4 shape, its routine to stick to the rule and show your 4 card suit.",
          rule: "With 6-4 shape we still rebid our 4 card suit.",
          extra: {
            lead: "This was the full hand",
            // As Paul gave it: 3 / 1087543 / A53 / K42 (13 cards).
            north: { S: "3", H: "10 8 7 5 4 3", D: "A 5 3", C: "K 4 2" },
            after:
              "The contract of 4H was a claimer, while 2S was going -1. While we won't always find partner with 6 cards in our second suit, 4+ is not uncommon at all.",
          },
        },
      ],
    },
    {
      id: "m1-3",
      video: "https://youtu.be/-Q0mkLYYlMQ", // "3 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "A Q J", H: "K Q 9 4 3", D: "A 9 5 2", C: "5" },
      start: OPEN_1H_1NT,
      steps: [
        {
          id: "b1",
          answer: "2D",
          comment:
            "We keep it simple and just bid our suit naturally again. This can contain up to 17 points. A lot of players make the mistake of thinking they need to show extra strength at this point. Most of the time you will have the opportunity to bid - we start by showing our shape, with plans to show a bit extra strength later.",
        },
        {
          id: "b2",
          answer: "2S",
          continuation: ["P", "2H", "P"],
          comment:
            "Under normal circumstances we would pass 2H, when we had say 12-14 points. Since we have extra strength, we are entitled to make another bid. The best bid will be the one that describes our hand. We have some length and strength in spades, we bid our 3rd suit naturally.\n\nPeople get confused about what such a bid might mean. Often when a player is confused they assume it means something strange - the typical and best meaning for bids is the natural meaning.\n\nWhy do we bid like this? It should make a big difference to partner, they now know we are short in clubs and will be able to evaluate their hand, and judge whether No trump is appropriate or not.",
        },
      ],
    },
    {
      id: "m1-4",
      video: "https://youtu.be/myh-qMiCVKA", // "4 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "K 10 3", H: "A Q 9 4 2", D: "K 5 4", C: "J 2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "Always rebid 1NT with a balanced hand rather than supporting partner with 3 cards when you can possibly have a 7 card fit only. This is the same or similar to a 1C and 1D opening, where we also rebid 1NT instead of supporting with 3, when balanced.",
        },
      ],
    },
    {
      id: "m1-5",
      video: "https://youtu.be/207Ld1HJM60", // "5 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "K 10 3", H: "A Q 9 4 2", D: "K J 5 4", C: "2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment:
            "This is an interesting moment and worth understanding well. I suggest thinking about these options. Two significant things have happened\n\n1. We have supported with 3 cards, into a possible 7 card fit.\n2. We have not rebid our diamonds, we have prioritised supporting partner.\n\nThis action of supporting partner instead of rebidding our suit applies when we have a standard range hand, 12-14 typically. In the upcoming example we will look at what happens when we are a bit stronger.",
        },
        {
          id: "b2",
          answer: "3D",
          continuation: ["P", "2N", "P"],
          comment:
            "Over partner's natural 2NT, we should bid our shape, there are possible bids that can be made here\n\na) Rebid spades if we had 4 (since bidding 2S can be 3 or 4, we should show the 4th if we had it which we do not)\nb) Rebid a 6th heart if we had it, which we also dont\nc) Rebid a 4 card suit partner does not know about\n\nWhen we bid 3D, partner knows we have 3 spades only, 5 hearts only, and 4 diamonds. Partner can basically write down our exact shape! Partner also knows we are not more than about 14 or 15 points, (next example will explain why).\n\nBy bidding naturally we have a hand that is completely predictable to partner, and they are well placed to determine the way forward.",
        },
      ],
    },
    {
      id: "m1-6",
      video: "https://youtu.be/wIsw7shfLms", // "6 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "K 10 3", H: "A K Q 9 2", D: "K J 5 4", C: "2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "2D",
          comment:
            "If you recall the earlier example (example 3) I said you can rebid your suits on the 2 level with up to 17 points. This hand is no exception. With enough strength (say 15-17) we first bid our 4 card suit. We then plan to have another go, showing our 3 spades later.\n\nWith extra strength we can take our time and bid our full hand out naturally, whereas when we are weaker, we often just have \"1 shot\" at the auction, and don't give ourselves as many bids to fully bid our shape.",
        },
        {
          id: "b2",
          answer: "2S",
          continuation: ["P", "2H", "P"],
          comment:
            "We just calmly bid 2S here, showing naturally our 3 card support. In addition we are showing that we are strong - because otherwise we would've just bid {{plain|2S}} the first time! (see previous example)\n\nI recommend redoing this problem and problem 5, as they are key ideas that take solidifying\n\nRULE: With a weak hand just support immediately with 3 cards\n\nRULE: With a strong hand, first show your other 4 card suit then later show your three card support.\n\nOne sidenote that is critical to not be confused about: \"Golden rule of bridge is to support partner\" That is when we know we have an 8 card fit. Here we do not have \"support\", we only have secondary support, we may only have a 7 card fit.\n\nRULE: When we know we have an 8 card fit with partner, that should always be shown as a matter of priority",
        },
      ],
    },
    {
      id: "m1-7",
      video: "https://youtu.be/wfLCMLuWO0U", // "7 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      article: "art2nt",
      south: { S: "K Q 10 8 4", H: "5", D: "A K Q 7 2", C: "A 5" },
      start: OPEN_1S_1NT,
      steps: [
        {
          id: "b1",
          answer: "3D",
          comment:
            "A jump by opener is game force, showing 18+ points typically, and is natural. This is a perfect hand to do it on. If, however we had a 4 card diamond suit only, we may still have to bid 3D. It is common amongst expert pairs to play a convention to show 5431 game forcing hands, and therefore leave the 3 level jumps for 5-5 only shape. It is not something I would stress over but if you want to push yourself, it might be worth thinking about - {{art:art2nt|here is an article on it}}.",
        },
      ],
    },
    {
      id: "m1-8",
      video: "https://youtu.be/AlMkBnQKbSs", // "8 M" on the channel, uploaded 2 Oct 2026 - shown at the end of the hand
      south: { S: "Q 10 2", H: "A Q J 9 4 3", D: "5", C: "A J 4" },
      start: OPEN_1H_1NT,
      steps: [
        {
          id: "b1",
          answer: "2H",
          comment:
            "With a 6 card suit and no 5 card suit, our rebids after opening 1H are either going to be 2H or {{plain|3H}}. What is the rule about which to bid?\n\nThe bid of 3H is appropriate if you fit in one of these two rules\n\n16+ points - 3H will be correct.\n\nOR\n\n7+ tricks. Sometimes you may have less points, but 7+ tricks - meaning your hand is quite effective despite a low point count. Lets look at an example",
          extra: {
            hands: [{ label: "7 tricks", hand: { S: "10 4 2", H: "A K 10 7 4 3", D: "5", C: "A K 4" } }],
            after:
              "We only have 14 points, but we can say reasonably that we will often have 5 heart tricks, and 2 club tricks = 7. Despite only having 14 points this would qualify for a 3H bid.",
          },
        },
      ],
    },
  ],
};


// PRACTICE SETS (Paul, 7 Oct 2026): short question sets attached under a category, shown beneath it on the
// Guided bidding page. Same trainer as a set; each "hand" is one question. A step with `choices` is a
// multiple-choice question instead of a bid. `extra` may be a list of blocks, each with an optional `title`
// and `auction` (shorthand calls, shown under the title). His words verbatim; grammar fixes listed in the
// session. No opponent bidding anywhere - the opponents pass throughout.
const OPEN_1S_1NT_2D_2S = ["-", "-", "-", "1S", "P", "1N", "P", "2D", "P", "2S", "P"];
const MAJOR_PRACTICE_1 = {
  id: "practice-1",
  kind: "practice",
  // Paul's heading was "Practice"; no set name given yet.
  title: "Practice set 1",
  hands: [
    {
      id: "p1-1",
      intro:
        "Let's look at this auction, from the previous section I mentioned that with up to 17 points, the normal, natural rebid on the 2 level is the right bid. Lets work on some follow ups. I will give you slightly different hands and I ask you to work out the bid",
      article: "shortage1",
      south: { S: "K Q 8 4 3", H: "2", D: "A J 5 2", C: "A K 3" },
      start: OPEN_1S_1NT_2D_2S,
      steps: [
        {
          id: "b1",
          answer: "3C",
          comment:
            "We simply bid our 3 card suit naturally - now partner knows our shape and is in an excellent position to decide what to do next. Imagine partner had one of these two, very different hands. We may belong in 3NT if partner has points opposite our singleton, or 5C or perhaps even 6C!",
          extra: [
            {
              title: "6C is a great contract",
              // North as Paul gave it was A2 / 532 / K4 / Q65432; the 2 of hearts and 3 of clubs are in South's hand,
              // so those two spot cards were changed (hearts 543, clubs Q76542) - flagged to Paul 7 Oct 2026.
              hands: [
                { label: "Partner (North)", hand: { S: "A 2", H: "5 4 3", D: "K 4", C: "Q 7 6 5 4 2" } },
                { label: "You (South)", hand: { S: "K Q 8 4 3", H: "2", D: "A J 5 2", C: "A K 3" } },
              ],
              after:
                "Hearing 3C was music to your ears when holding this hand - no wastage opposite partner's known singleton heart (can you see from the bidding that partner has a singleton heart, that is a key takeaway here!). We also have uncovered our 9 card club fit all of a sudden!",
            },
            {
              title: "3NT please!",
              hands: [
                { label: "Partner (North)", hand: { S: "J 2", H: "A Q J 10", D: "10 9 8", C: "J 9 4 2" } },
                { label: "You (South)", hand: { S: "K Q 8 4 3", H: "2", D: "A J 5 2", C: "A K 3" } },
              ],
              after:
                "We have all our points in partner's singleton. As I've said a lot on the site - Points opposite partner's shortage indicate No Trump is correct, keep the contract low. With 9 points however we should be bidding game here, 3NT, knowing partner is about 16 or 17 points.",
            },
          ],
        },
      ],
    },
    {
      id: "p1-2",
      south: { S: "K Q 8 4 3", H: "K 2", D: "A J 5 2", C: "A 3" },
      start: OPEN_1S_1NT_2D_2S,
      steps: [
        {
          id: "b1",
          answer: "2N",
          comment:
            "This bid shows that we have about 16-17 points, and we have values in both the unbid suits, 5422 is certainly going to be our shape.",
        },
      ],
    },
    {
      id: "p1-3",
      intro:
        "Lets change the hand and bidding slightly, this time partner has bid a suit. We are building from the same concepts though",
      south: { S: "K 3 2", H: "K Q 8 4 3", D: "A J 5 2", C: "2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment: "We can support with 3 cards only when we are unbalanced in the 12-14 range.",
        },
      ],
    },
    {
      id: "p1-4",
      south: { S: "K 3 2", H: "K Q 8 4 3", D: "A 5 2", C: "J 2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "Balanced we should always rebid 1NT rather than support with 3 cards (into a potential 7 card fit. Remember though, 3 card support is important to show if you have a known 8 card fit! i.e. if partner shows a 5 card suit).",
        },
      ],
    },
    {
      id: "p1-5",
      south: { S: "K 4 3 2", H: "K Q 8 4 3", D: "A 2", C: "J 2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment:
            "Of course with 4 cards, a known 8 card fit, always support... always.\n\nYou are in the 12-14 range so a suitable strength for this simple raise.",
        },
      ],
    },
    {
      id: "p1-6",
      // Paul gave KJ43 / KQ843 / A2 / 2 = 12 cards. Diamonds made A52 (4-5-3-1, same points) - flagged 7 Oct 2026.
      south: { S: "K J 4 3", H: "K Q 8 4 3", D: "A 5 2", C: "2" },
      start: OPEN_1H_1S,
      steps: [
        {
          id: "b1",
          answer: "3S",
          comment:
            "You should bid 3S with 4 card support and an unbalanced hand - count 3 points for your singleton when you have a known 8 card fit, if you are 15+ points total including distribution, 3S is a good idea! Remember 2S shows 12-14 style range.\n\nContrast that to the previous example.",
          extra: [
            {
              auction: ["1H", "1S", "2S"],
              hands: [{ label: "You (South)", hand: { S: "K 3 2", H: "K Q 8 4 3", D: "A J 5 2", C: "2" } }],
              after:
                "Here we did not count 3 points for our singleton because we do not have a fit yet, only 7 cards.\n\nRULE: You can only count points for distribution when you have a known fit of 8+ cards.",
              // Paul's "5 points for void, 3 for singleton, 1 for doubleton", shown as tiles (7 Oct 2026: "make it look nice").
              tiles: [
                { big: "5", label: "points for a void" },
                { big: "3", label: "points for a singleton" },
                { big: "1", label: "point for a doubleton" },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "p1-7",
      // No hand: a question about the auction itself (South opened 1C, raised partner's 1S to 2S).
      start: ["-", "-", "-", "1C", "P", "1S", "P", "2S"],
      steps: [
        {
          id: "q1",
          question:
            "Opener started with 1C then simply raised their partner's spades, what strength range should we expect there?",
          choices: ["(11)12-14", "14-16", "15-16", "12-17"],
          answer: "(11)12-14",
          comment:
            "Just the simple standard 12-14 points. With these ranges we\n\nopen 1C and rebid 1NT frequently\n\nOr\n\nOpen 1C and just do a simple raise of partner's suit",
          rule:
            "(11) 12-14 is the most common range, feel the rhythm of the very frequent and common auctions, they are the basis to good bidding and getting to good contracts.",
        },
      ],
    },
    {
      id: "p1-8",
      start: ["-", "-", "-", "1D", "P", "1H", "P", "2H"],
      steps: [
        {
          id: "q1",
          question: "We said this is always 12-14, how many cards though?",
          choices: ["2H is always 4 card raise", "2H can be 3 card raise"],
          answer: "2H can be 3 card raise",
          comment: "2H can be 3 or 4 card raise, always 12-14",
        },
      ],
    },
    {
      id: "p1-9",
      start: ["-", "-", "-", "1C", "P", "1H", "P", "1N"],
      steps: [
        {
          id: "q1",
          question: "Opener, who has rebid 1NT, will",
          choices: ["Not normally have 3 card hearts", "Could easily have 3 card hearts"],
          answer: "Could easily have 3 card hearts",
          comment: "Could easily, opener is balanced and will routinely bid 1NT with 3 cards.",
        },
      ],
    },
    {
      id: "p1-10",
      // East overcalls 2S - the first opponent bid in this set.
      south: { S: "A 5 4 3", H: "K 9 4 2", D: "J 3", C: "A 5 2" },
      start: ["-", "-", "-", "1C", "P", "1H", "2S"],
      steps: [
        {
          id: "b1",
          answer: "3H",
          comment:
            "We would like to just bid 2H showing 12-14, but we are willing to take the push to the 3 level in competition with 4 card support - we would rather push a bit and show our fit, rather than be silent and not let partner know about it - Bridge is all about finding fits!",
        },
      ],
    },
  ],
};

// 2 over 1 (Paul, 9 Oct 2026). His words VERBATIM; fixes listed to him: "frame work" -> framework, "try move" -> "try to
// move", "about A good 15" -> "about a good 15", "Somewhat trick question" -> "Somewhat of a trick question", "Open 1NT"
// -> "open 1NT" mid-sentence. North deals in hands 1-4, 6 and 8 (`start` = W blank, North's opening, East's pass); South
// deals in hands 5 and 7. "AI insert rule" lines are step `rule`s; hand 3's tick/cross list is `options`; hand 4's
// "emphasise with colour" is {{em|...}}. Published 9 Oct 2026 with his videos 1-8.
const NORTH_1S = ["-", "1S", "P"];
const NORTH_1H = ["-", "1H", "P"];
const TWO_OVER_ONE = {
  id: "responder-rebids",
  // Paul's heading was "2 over 1 auctions" (the category); no separate set name given yet.
  title: "2 over 1 auctions",
  note:
    "I like teaching 2 over 1 because, in the framework I suggest, you will get plus scores far more often, and avoid -50, -100 creeping in. In my experience the vast majority of expert pairs play 2 over 1, and its easy to learn for the beginner because of simple repeatable rules.\n\nIf you do not play 2 over 1, don't be discouraged - I suggest still playing through these hands.",
  hands: [
    {
      id: "t21-1",
      video: "https://youtu.be/FIzdy6wt-WU", // "1" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      south: { S: "Q 5", H: "A J 5 4", D: "K J 9 2", C: "8 4 3" },
      start: NORTH_1S,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "It is your first turn to bid as responder - bidding on the 2 level would be game forcing. 11 points is not enough for that, so we simply bid 1NT.",
          rule: "Do not bid a new suit on the 2 level at your first turn unless you have enough to game force.",
        },
        {
          id: "b2",
          answer: "2N",
          continuation: ["P", "2C", "P"],
          comment:
            '2NT shows 11-12 points and values/stoppers in the unbid suits.\n\nThis is a very important auction to know, only rebid 2NT when you are "highly invitational", with 10 or less points we do something else. Continue to next hand to find out what.',
        },
      ],
    },
    {
      id: "t21-2",
      video: "https://youtu.be/_r6D2gXJVw0", // "2" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      south: { S: "Q 5", H: "A J 5 4", D: "Q 10 9 2", C: "8 4 3" },
      start: NORTH_1S,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment: "Fairly standard 1NT response, whether you are playing 2 over 1 or not.",
        },
        {
          id: "b2",
          answer: "2S",
          continuation: ["P", "2C", "P"],
          comment:
            "We cannot bid 2NT, since as mentioned that requires 11-12 points. We just simply bid 2S with 2 card support (we have already denied 3 card support earlier when we did not raise 1S to {{plain|2S}}).\n\nWhat about simply passing? As mentioned in the {{plain|1H}}/{{plain|1S}} series, opener can make a simple rebid of 2C with up to 17 points. With about 8/9+ points, we should generally not pass. By far the most common response in that range will be bidding 2 of the openers suit, like we did here with 2S.",
          rule: "With 9+ points do not pass openers second bid since they can be up to 17 points.",
        },
      ],
    },
    {
      id: "t21-3",
      video: "https://youtu.be/eUd8CFE3Ts8", // "3" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      south: { S: "J 5", H: "A 6 5", D: "K J 5 4 2", C: "10 4 2" },
      start: NORTH_1S,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "Getting into the rhythm of responding 1NT, you will be doing it a lot! Trust it, it will get you good scores.",
        },
        {
          id: "b2",
          answer: "2S",
          continuation: ["P", "2C", "P"],
          comment: "We are faced with multiple options.",
          // Paul: "(ai put cross) ... (tick) 2S" - each alternative with his verdict.
          options: [
            { call: "P", ok: false, text: "We cannot pass with 9+ points as mentioned previously." },
            {
              call: "2D",
              ok: false,
              text: "If we bid a new suit, we have to be ready to play it opposite a singleton. Partner has already shown 5 spades and 4 clubs, they will often have 1 diamond. Rather play in your 7+ card spade fit than suggest this inadequate diamond suit.",
            },
            { call: "3C", ok: false, text: "Raising this suit should show a real fit, 4+ cards is needed to raise." },
            { call: "2N", ok: false, text: "We need 11-12 for 2NT" },
            { call: "2S", ok: true, text: "this should be a very common rebid for you." },
          ],
          rule: "Your most frequent rebid in these auctions is to return to opener's first bid suit, here that is spades.",
        },
      ],
    },
    {
      id: "t21-4",
      video: "https://youtu.be/Y6fxR04Vtls", // "4" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      south: { S: "A 4 2", H: "9", D: "K Q 10 5 4 2", C: "10 4 2" },
      start: NORTH_1H,
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment: "Should be getting in the habit of this by now, {{em|Frequently respond 1NT}}",
        },
        {
          id: "b2",
          answer: "2D",
          continuation: ["P", "2C", "P"],
          comment:
            "6+ card suits are candidates to bid here. We expect a singleton from partner but that is okay - and on a good day we might find 2 or 3 card support!",
        },
        {
          id: "b3",
          answer: "3N",
          continuation: ["P", "2N", "P"],
          comment: "Partner's 2NT is invitational showing about (15)16-17 points.",
          rule:
            '2NT is never used as a bid to say "I don\'t like your suit" - it is always used to show extra strength. 2NT is not a contract we want to be in without a lot of combined strength.',
        },
      ],
    },
    {
      id: "t21-5",
      video: "https://youtu.be/hO6hW9JtW6M", // "5" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      // South opened 1H here (Paul: "ai give south Q43 A9872 2 AK103").
      south: { S: "Q 4 3", H: "A 9 8 7 2", D: "2", C: "A K 10 3" },
      start: ["-", "-", "-", "1H", "P", "1N", "P"],
      steps: [
        {
          id: "b1",
          answer: "2C",
          comment: "As always we routinely bid our second suit, clubs.",
        },
        {
          id: "b2",
          answer: "P",
          continuation: ["P", "2D", "P"],
          comment:
            'Partner is bidding this expecting to be opposite a singleton. As shown in the previous hand, we do not bid 2NT to say "I don\'t like your suit", we only bid 2NT to show a strong invitational hand about a good 15 to 17 points, not 14.',
          rule:
            "When partner shows a suit here, they have a serious suit and you should not try to move the contract, pass will be your most common option by far with ordinary hands.",
        },
      ],
    },
    {
      id: "t21-6",
      video: "https://youtu.be/Qw_sRNXhvZw", // "6" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      // No hand-level article: the Fourth Suit Forcing link appears only in bid 2's commentary, where it is mentioned
      // (Paul, 9 Oct 2026: "dont link ... till it is mentioned").
      south: { S: "10 8 4 2", H: "9", D: "A Q 10 8 5", C: "J 10 9" },
      start: NORTH_1H,
      steps: [
        {
          id: "b1",
          answer: "1S",
          comment:
            "If we have a major we can bid on the 1 level, do it - its the best you can do with a weak hand. You cannot bid your diamonds as that would have required going on the 2 level.",
        },
        {
          id: "b2",
          answer: "P",
          continuation: ["P", "2C", "P"],
          comment:
            "Just pass, you have a decent dummy for clubs. You cannot bid your diamonds for a few reasons.\n\n1. It would be 4th suit game forcing! ({{art:fourthSuit|Fourth Suit Forcing: How and When to Use It}})\n2. You are fine in clubs, you have 3 card support and a singleton, this will be a useful dummy.",
          rule: 'Often consider passing when you are weak and misfitting, keep the contract low, "get off the train"!',
        },
      ],
    },
    {
      id: "t21-7",
      video: "https://youtu.be/KCMlnmVL7OI", // "7" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      // South deals and opens.
      south: { S: "A Q", H: "A 8 4 3 2", D: "K 9 3 2", C: "K 10" },
      start: ["-", "-", "-"],
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "Somewhat of a trick question to keep you thinking. You can open 1NT with 5422 shape, the indication is in how many points are in your short suits. Here we have more than half our points there, it strongly indicates 1NT. When we have about 7+ points in our doubletons we should consider 1NT.",
        },
      ],
    },
    {
      id: "t21-8",
      video: "https://youtu.be/aqzZcL7clmI", // "8" on the channel, uploaded 9 Oct 2026 - shown at the end of the hand
      south: { S: "K 10", H: "5", D: "Q 9 4 3 2", C: "A 8 5 4 2" },
      start: NORTH_1S, // Paul, 9 Oct 2026: hand 8 is a 1S opening (he first wrote 1H)
      steps: [
        {
          id: "b1",
          answer: "1N",
          comment:
            "As always, 1NT when we aren't good enough to game force, and don't have a suit to bid on the 1 level.",
        },
        {
          id: "b2",
          answer: "3H",
          continuation: ["P", "2D", "P"],
          comment:
            "Discuss with partner - jumps here are typically splinters, showing shortage in the jumped suit, 4+ card support, and an excellent hand.\n\nOn the hand evaluation, its worth paying attention to the reasons why this hand is excellent\n\n1. Spade values - the Kx will bring to life partners hand, say for example partner has AQxxx - that turns it into 4-5 tricks! (Points in partners long suits = good!)\n\n2. huge 5 card support - at least a 9 card fit. Big fits (9+ cards) start making leaps in terms of what contracts you can make. It is not just 1 more boring trump, it matters a lot.\n\n3. Shortage and a side 5 card suit.",
        },
      ],
    },
  ],
};

// DOUBLE OR NOT (Paul, 10 Oct 2026): "dont want to make it live for a while ... just dont make it public" - the
// category is added ONLY on the local dev copy (localhost); the live site never sees it, not even for admins.
// Paul (10 Oct): not live for several days; videos coming. To publish: drop the LOCAL_DEV guard on the category below (+ videos / Game Plan if he wants them).
// His words verbatim; grammar fixes listed in the session. Paul labelled two hands "Hand 8" - the second is hand 9 here.
const DOUBLE_OR_NOT = {
  id: "double-or-not",
  title: "Double or not",
  hands: [
    {
      id: "dn-1",
      video: "https://youtu.be/vsU0m-eC_AM", // "Double or not - Hand 1" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "A Q 9 2", H: "K 4", D: "10 8 4 3", C: "7 6 2" },
      start: ["-", "1D", "1H"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "{{em|To discuss with your partner!}} What is X and what is {{plain|1S}}? In this position many people play X (a double by responder is often called a negative double) as showing exactly 4 cards in the other major and a bid of {{plain|1S}} as showing 5+ cards.\n\nAlso worth noting, the golden rule is to support partner and we have support - yet I still recommend trying to uncover a major fit first, while you have a chance.",
        },
        {
          id: "b2",
          answer: "3D",
          continuation: ["2H", "P", "P"],
          comment:
            "Here we should not hesitate to bid 3D, showing our support for partner's suit and competing for the part score - don't let the opponents comfortably play in a 2 level fit.",
        },
      ],
    },
    {
      id: "dn-2",
      video: "https://youtu.be/96i41j9m4ug", // "Double or not - Hand 2" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      // West deals.
      south: { S: "2", H: "A Q 10 4", D: "5 4", C: "A K 10 8 4 2" },
      start: ["1D", "P", "1S"],
      steps: [
        {
          id: "b1",
          answer: "2C",
          comment: "With 6 card suits just bid them, or with other highly distributional hands such as 5-5 shape.",
        },
        {
          id: "b2",
          answer: "X",
          continuation: ["2S", "P", "P"],
          comment:
            "Here we can double - Partner already knows we have long clubs from [[3|our first bid]], our double now shows new information about our hand - the fact that we have a good hand, and shows our heart suit.\n\nPartner will not be surprised at all to find out we have 6 clubs and 4 hearts, our bidding basically telegraphed that.",
        },
      ],
    },
    {
      id: "dn-3",
      video: "https://youtu.be/ioEHZ2JNxWk", // "Double or not - Hand 3" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "K 10 8 4 3", H: "Q J 9 5 2", D: "3", C: "J 10" },
      start: ["-", "1C", "1D"],
      steps: [
        {
          id: "b1",
          answer: "1S",
          comment:
            "With 5-5 shape, we do not double, we just bid the suits naturally in the same way we would have if we opened. Open the higher of two 5 card suits (a strange thing about bridge).",
        },
        {
          id: "b2",
          answer: "2H",
          continuation: ["2D", "P", "P"],
          comment:
            "The main idea is that 2H is not forcing, and you should certainly bid it on this hand - only responders first new suit bid is forcing (1S was forcing). Article is linked below ({{art:forcing|Is that bid forcing? Responder's first bid.}}).\n\nBridge is about finding fits, with distributional hands you should be trying to show your suits if you can, especially on the 2 level, maybe not so much on the 5 level! Distributional hands can come alive if you find a big fit, even when point count is scarce.",
        },
      ],
    },
    {
      id: "dn-4",
      video: "https://youtu.be/qFxIFfB2ZAs", // "Double or not - Hand 4" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "8 4 2", H: "A Q 10 5", D: "10 8", C: "A Q 10 2" },
      start: ["-", "1S", "3D"],
      steps: [
        {
          id: "b1",
          answer: "4D",
          accept: ["4S"], // Paul, 10 Oct 2026: "count 4S correct"
          comment:
            "Simply raise spades, or better yet a cue raise of 4D to show about 11+ points and spade support. The key point is, do not double and go looking for other fits or whatever the purpose of double is supposed to be - you have a known fit, let your partner know also!",
        },
      ],
    },
    {
      id: "dn-5",
      video: "https://youtu.be/Hdn2Q2ymDeE", // "Double or not - Hand 5" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      // West deals. Paul gave 14 cards (A1084 92 AK54 J1032); 10 Oct 2026 he said take away the 2 of clubs.
      south: { S: "A 10 8 4", H: "9 2", D: "A K 5 4", C: "J 10 3" },
      start: ["1H", "X", "3H"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "Here we should double, telling partner that we have the points to bid on but are not sure about what the contract should be. [[1|Partner's original double]] showed 3-4 spades, we do not want to commit to spades and be in a 4-3 fit. Partner might for example bid {{plain|3NT}} here with only 3 spades and values in hearts, which we would pass.",
        },
      ],
    },
    {
      id: "dn-6",
      video: "https://youtu.be/AIEcs71D5GE", // "Double or not - Hand 6" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "7 2", H: "A K 3", D: "Q J 10 9", C: "K 8 4 2" },
      start: ["-", "1H", "4S"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            'We love to support if possible, but to go to the 5 level with an 8 card fit is typically going to be a bad idea. Double here does not say to partner "I have 5 solid spades sitting over here", it just says I have about an opening hand but nothing special that I want to move to the 5 level.\n\nOnly move to the 5 level with much more distributional hands - voids in the opponents suits are good candidates!',
        },
      ],
    },
    {
      id: "dn-7",
      video: "https://youtu.be/GMfcDgnNn7Q", // "Double or not - Hand 7" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "A Q 10 8", H: "5", D: "10 8 4 2", C: "9 5 3 2" },
      start: ["-", "1H", "4S"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "Just pass and defend, you are almost certainly going to get a positive score. On a good day partner will reopen with a double, as he is certain to have short spades, and perhaps enough points. If Partner doubles you will know what to do... pass again.\n\nThe key point is that double now by you would be more to the side of takeout - like the previous hand. Partner with a highly distributional hand may decide to move rather than defend, which is not what you want.",
        },
      ],
    },
    {
      id: "dn-8",
      video: "https://youtu.be/wAtRpddTX60", // "Double or not - Hand 8" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "Q 10 8 4", H: "2", D: "A J 5 2", C: "J 10 9 4" },
      start: ["-", "1C", "4H"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "We have perfect shape for doubling, and we would gladly do so on the 2 level. Bidding on the 4 level would be overdoing it - we likely have a combined 20 points, with an 8 card fit if we are lucky, what are we doing on the 4 level with that?",
        },
      ],
    },
    {
      id: "dn-9",
      video: "https://youtu.be/H4YO3Xz7S5A", // "Double or not - Hand 9" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "K Q 9 8", H: "8 6 5 4 2", D: "5", C: "Q 10 3" },
      start: ["-", "1C", "2D"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "Normally I advocate just bidding your 5 card suit, but there are two good reasons why double is more appropriate here\n\n1. You are weak and just want to compete to the 2 level if possible, you don't want to get too high. Bidding {{plain|2H}} is forcing and will drive the auction up too high.\n\n2. Your hearts are very poor and your spades are good. Occasionally in such situations you can treat your very poor 5 card suit as a 4 card suit.",
        },
      ],
    },
  ],
};

// COMPETING (Paul, 14 Oct 2026): "I don't want to release it yet ... will release them both in a couple days. Just want to
// test and make videos" - LOCAL ONLY, like DOUBLE_OR_NOT. His words verbatim; grammar fixes listed in the session:
// "WIth" -> "With", "try remember" -> "try to remember", "safter" -> "safer", "t he" -> "the".
// Seats: North deals unless noted (`start` begins "-" for West). Hands 2, 5 and 9: South deals (South's own earlier
// calls are in `start`). Hands 10 and 11: East opens 1S - Paul wrote "1S by south" in hand 10; read as East (RHO),
// since South holds the singleton spade and doubles it - flagged.
// Hand 3's four auctions are separate "hands" (comp-3a..3d), each one Yes/No question.
// Hand 7's partner hand: Paul gave AJ432 / 2 / 85 / AK932, but South holds the 2 of spades and the 2 of hearts, so
// North is shown as AJ543 / 3 / 85 / AK932 - flagged.
const OPP_EAST_1S = ["-", "-", "1S"];
const COMPETING = {
  id: "competing",
  title: "Competing",
  reading: "forcing",
  note:
    'A rule that will unlock your competitive bidding potential is to remember\n\n**"Responder\'s first change of suit is forcing, beyond that, simple changes of suit are not forcing"**\n\nThere is one exception which will be covered.',
  hands: [
    {
      id: "comp-1",
      video: "https://youtu.be/y7H9EUK0ms8", // "Competing - Hand 1" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "K J 8 4 2", H: "5", D: "Q J 10 4 3", C: "9 2" },
      start: ["-", "1C", "1H"],
      steps: [
        {
          id: "b1",
          answer: "1S",
          comment:
            "A simple 1S is going to be correct when you have 5 cards. What about double? Double typically shows 4 spades only (and nothing about diamonds), subject to partnership agreement. But even if it did show spades and diamonds, I would not suggest it as it does not show 5-5.",
          rule: "With distributional hands, which includes 5-5 shape, 6 card suits, try not to double, just bid your suits naturally.",
        },
        {
          id: "b2",
          answer: "3D",
          continuation: ["2H", "P", "P"],
          comment:
            "You can bid here despite not having many points, as it is not forcing. Responder's second bid is not forcing",
        },
      ],
    },
    {
      id: "comp-2",
      video: "https://youtu.be/FXr00Omq4g0", // "Competing - Hand 2" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      // South deals and opens 1C.
      south: { S: "K J 10", H: "5 4", D: "J 5 2", C: "A K 8 5 2" },
      start: ["-", "-", "-", "1C", "1S", "2H", "P"],
      steps: [
        {
          id: "b1",
          answer: "2N",
          comment:
            "When we open a balanced (11) 12-14, and partner forces us, we typically want to bid NT at the lowest level possible. Here it also shows a spade stopper.\n\nOn the note of forcing bids: Partner (responder) bid is forcing. In fact bidding on the 2 level should naturally require a bit more from responding than bidding on the 1 level - 2H already shows a reasonably strong hand, not quite game forcing but close.\n\nWhy does it have to be strong? Partner is forcing the bidding up, typically to the 3 level. We can't be weak and do that..",
          rule: "When partner has already shown a strong hand by making their first bid on the 2 level, any subsequent change of suit tips them over the edge, and is forcing.",
        },
        {
          id: "b2",
          answer: "3N",
          continuation: ["P", "3D", "P"],
          comment:
            "With almost nothing in partners suits, and 11 of 12 points in the black suits, 3NT should stick out like a sore thumb.\n\nImportantly - Partner's 3D is forcing - why? This is the rule I just mentioned, given that partner's first bid was on the 2 level (already showing say 10+ points), any further change of suit is now forcing.",
        },
      ],
    },
    {
      id: "comp-3a",
      start: ["-", "1C", "P", "1S", "2H", "P", "P", "3D"],
      steps: [
        {
          id: "q1",
          question: "Is responders 3D bid forcing or not?",
          ringCall: 7,
          choices: ["Yes", "No"],
          answer: "No",
          comment: "The first bid was on the 1 level, the next bid is just bidding their other suit, it is not forcing.",
        },
      ],
    },
    {
      id: "comp-3b",
      intro: "Lets cement this idea with a few examples, and make it clear.",
      start: ["-", "1C", "1S", "2H", "2S", "P", "P", "3D"],
      steps: [
        {
          id: "q1",
          question: "Is responders 3D bid forcing?",
          ringCall: 7,
          choices: ["Yes", "No"],
          answer: "Yes",
          comment: "Since responder's first bid showed strength, 10+ points, a further change of suit is forcing.",
        },
      ],
    },
    {
      id: "comp-3c",
      start: ["-", "1C", "2D", "2S", "P", "2N", "P", "3H"],
      steps: [
        {
          id: "q1",
          question: "Is responders 3H bid forcing?",
          ringCall: 7,
          choices: ["Yes", "No"],
          answer: "Yes",
          comment: "Again, the first bid was on the 2 level, showing a good hand, subsequent change of suit is forcing.",
        },
      ],
    },
    {
      id: "comp-3d",
      start: ["-", "1D", "P", "1H", "2S", "P", "P", "3C"],
      steps: [
        {
          id: "q1",
          question: "Is responders 3C bid forcing?",
          ringCall: 7,
          choices: ["Yes", "No"],
          answer: "No",
          comment: "Since responder's first bid was on the 1 level, they can change suit without forcing.",
        },
      ],
    },
    {
      id: "comp-4",
      // You responded 1S; partner (North) opened 1D.
      start: ["-", "1D", "P", "1S", "2H", "3C", "P"],
      steps: [
        {
          id: "q1",
          question: "Is partner's 3C forcing?",
          ringCall: 5,
          choices: ["Yes", "No"],
          answer: "No",
          comment:
            "When it comes to the question of is a change of suit forcing, try to remember the original rule: Only responders first suit is forcing, all other changes of suit aren't forcing *other than the exception mentioned.\n\nSo, opener is allowed to bid their 5-5 hands - bridge is about bidding our suits and competing - potentially unlocking fits.",
        },
      ],
    },
    {
      id: "comp-5",
      video: "https://youtu.be/5xfsOCUxQD8", // "Competing - Hand 8" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      // South deals and opens 1C.
      south: { S: "4 2", H: "A J 10", D: "J 4 3 2", C: "A Q 10 4" },
      start: ["-", "-", "-", "1C", "P", "1S", "2H"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "You aren't forced to bid here, since partner will get another turn no matter what you do. Pass by you communicates that you are just a basic opening hand (12-14).",
        },
        {
          id: "b2",
          answer: "2N",
          continuation: ["P", "X", "P"],
          comment:
            "Just a natural 2NT, you have a double stopper in hearts so No trump is a good description\n\nPartner's double is takeout - you should aim to describe your hand as best as possible over that. Now partner knows we are 12-14 with heart stopper(s).",
        },
      ],
    },
    {
      id: "comp-6",
      video: "https://youtu.be/eMLPhR3BKBg", // "Competing - Hand 9" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "5 2", H: "K Q 10 4", D: "J 5 4 2", C: "Q 10 8" },
      start: ["-", "1C", "2S"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "Competing is a good idea, you have at least 20 points with partner- make a takeout double and encourage partner to get involved in the part score battle.\n\n*The indication that it is correct to double is that you have 0-2 cards in the opponents spade suit, that is the typical precondition for a takeout double.",
        },
        {
          id: "b2",
          answer: "P",
          continuation: ["P", "3H", "P"],
          comment:
            "Your initial double fully captured your hand, you don't have any extra - passing is your logical choice. Partner's 3H is just a 12-14 point hand with 4 hearts.",
        },
      ],
    },
    {
      id: "comp-7",
      video: "https://youtu.be/h59d6XmOfm4", // "Competing - Hand 10" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "K 2", H: "9 8 4 2", D: "A 4 2", C: "Q 10 8 4" },
      start: ["-", "1S", "P", "1N", "2H", "3C", "3H"],
      steps: [
        {
          id: "b1",
          answer: "5C",
          comment:
            "Your hand is great, points in the right places - no wastage in hearts, 4 card support. Don't just compete, when you find a big fit think about game if your points are well placed. Partner has this minimum point count hand and you make 6 when the spades behave!",
          extra: [
            {
              hands: [{ label: "Partner (North)", hand: { S: "A J 5 4 3", H: "3", D: "8 5", C: "A K 9 3 2" } }],
            },
          ],
        },
      ],
    },
    {
      id: "comp-8",
      video: "https://youtu.be/gBqVf6vd7tI", // "Competing - Hand 11" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "Q J", H: "A 4 2", D: "Q 9 8 5", C: "K 10 3 2" },
      start: ["-", "P", "1S"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "This is a poor takeout double for a couple reasons.\n\n1. Partner is passed so the upside is lower, and the downside is higher\n\n2. It is a minimum, and 3 of the points are in the opponents suit which is not good for your declaring chances (points in the opponents suits often indicate defending is a good idea, declaring upside is much lower)\n\n3. You only have 3 hearts, it is more attractive to make a takeout double with 4 hearts. Having 3 is fine, but if the hand is marginal or not so great, 3 hearts is an additional reason to just pass.",
        },
      ],
    },
    {
      id: "comp-9",
      video: "https://youtu.be/IH74SjtjORI", // "Competing - Hand 12" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      // South deals and passes; West opens 1C.
      intro: "If you pass 2S is the contract, do you compete or let them play there?",
      south: { S: "A K", H: "Q 10 4 3", D: "J 9 5 2", C: "9 4 2" },
      start: ["-", "-", "-", "P", "1C", "P", "1S", "P", "2S", "P", "P"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "Most of the time we want to compete for the part score, yet this is not the right hand to do it on. Why?\n\n1. 7/10 points are in the opponents suit, a strong sign to defend. If we declare without points in any of our suits.\n\nRule: Our declaring prospects drop a lot when points are not in our suits (they are in the opponents suit).\n\n2. The opponents may also only be in a 7 card fit. That would mean they have a decent chance of going down, and bidding could land you in a bad spot\n\nRule: If the opponents have big fits it is often safer to enter the auction, if they are misfitting (only a 7 card fit) it can be much more risky to enter.",
        },
      ],
    },
    {
      id: "comp-10",
      video: "https://youtu.be/Xp7A2W3K_44", // "Competing - Hand 13" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "5", H: "Q 10 8 4", D: "K J 9 2", C: "K 10 4 3" },
      start: OPP_EAST_1S,
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "A takeout double should be pretty close to opening hand values. Maybe shaded by a point or so with very good shape. This hand has good shape for it, but the point count is too light - partner will get the wrong picture.",
        },
        {
          id: "b2",
          answer: "X",
          continuation: ["2S", "P", "P"],
          comment:
            "Here we can and should double, compete for the partscore. You might be worried about your strength, but partner is marked with points as well - (as the opponents are limited or else they would've bid further!).\n\n1. We have great shape for competing, particularly the singleton in the opponents suit\n\n2. The opponents are known to have an 8 card fit.\n\nIt is worth noting, we are in whats called the \"passout seat\" which means our pass would be a third pass, so the contract would be set.\n\nLets take that rule and challenge it a bit with a similar idea in the next hand",
          rule: "In the passout seat you can compete with less points than normal.",
        },
      ],
    },
    {
      id: "comp-11",
      video: "https://youtu.be/bazAuwR8Kzk", // "Competing - Hand 14" on the channel, uploaded 14 Oct 2026 - shown at the end of the hand
      south: { S: "5 2", H: "A Q 10 4", D: "Q J 9 3", C: "K 10 2" },
      start: OPP_EAST_1S,
      steps: [
        {
          id: "b1",
          answer: "X",
          comment: "A regular takeout double, fairly straight forward.",
        },
        {
          id: "b2",
          answer: "P",
          continuation: ["2S", "P", "P"],
          prompt: "You are in the passout seat, should you bid on or let them play in 2S?",
          comment:
            "This time it is a pass, why?\n\nThe concrete reason is - you have already shown your hand with the first double. Partner could have acted if she wanted to, she could've competed over 2S. Partner had all the information, yet decided to pass.\n\nIt turns out that partner had a 3334 shape hand, with most of the points in spades. 2S went down, and also declaring on the 3 level for your side would be a disaster. Just follow the principle of \"have I bid my hand?\" - if so, you typically won't need to do more.",
          rule: "Bid your hand, once you have bid it, and partner knows what it is, you do not need to bid it twice - trust your partners decision.",
        },
      ],
    },
  ],
};

// COMPETE MORE WHEN ITS RIGHT TO DO SO (Paul, 29 Oct 2026; published 30 Oct 2026).
// His words verbatim; grammar fixes listed in the session. His x's are filled with real spot cards not held by South.
// Hand 6 / hand 8 continuation: "nil vul" -> hand.vul. Hand 4's support-doubles note is the hand intro with the article link.
const COMPETE_MORE = {
  id: "compete-more",
  title: "Compete more when its right to do so",
  hands: [
    {
      id: "cm-1",
      video: "https://youtu.be/7zC8G9Iroyg", // "Compete more - Hand 1" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      south: { S: "2", H: "Q 8 4 3", D: "K 9 5 2", C: "J 10 8 2" },
      start: ["-", "1C", "3S"],
      steps: [
        {
          id: "b1",
          answer: "P",
          comment:
            "You have perfect shape for a double here, the question is - do we have enough?\n\nPass: We don't have enough. Assume partner has say 12 points, some regular balanced hand. We would end up over reaching by pushing to the 4 level.\n\nVery often the goal when we have 20 or so points in total combined, is to declare a partscore on the 2 level, and leave the 3 level for the opponents. This should fit into that type of hand.\n\nIf partner does not have a boring 12-14 balanced hand, they still might make a bid (80-90% of the time they will have the boring 12-14 hand when they open 1C).",
          aside:
            "Very important note: The word \"negative double\" is used a lot and can be very confusing. It is the common word used for the double made by responder - (here you are responder). I've always preferred to just describe these doubles as \"takeout\". Here the double ideally should be the shape we have, tolerance/support for every suit.\n\nIt's worth noting, however, that a double on the 1 level by responder often only shows 4 cards in the other major. You can afford to assign it that meaning on the 1 level (because if you don't have a fit, you can just retreat to 1NT or some other low level contract). However, on higher levels, the doubles should typically be genuine takeout style doubles.",
        },
        {
          id: "b2",
          answer: "5C",
          continuation: ["P", "4C", "P"],
          comment:
            "Here you have 4 card support, a singleton, and a potentially useful couple cards. You can certainly give a simple raise to 5C. If partner can bid to 4C alone, you should have more than enough. 5C makes comfortably.",
          extra: [
            {
              title: "5C making",
              north: { S: "7 6", H: "K 7", D: "A 4", C: "A K 9 7 6 5 4" },
            },
          ],
        },
      ],
    },
    {
      id: "cm-2",
      video: "https://youtu.be/HRrY9R2kOIg", // "Compete more - Hand 2" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      south: { S: "2", H: "Q 8 4 3", D: "K 9 5 2", C: "J 10 8 2" },
      start: ["-", "1C", "2S"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "Here we have perfect takeout shape, there's a good chance we will have an 8+ card fit (similar to last hand). The difference is that when we have around 20ish points combined, we should be willing to compete to the 3 level, not just sell out every time to a 2 level contract.\n\nNote: If you had length in spades, you are okay to sell out because defending can be profitable! for example\n\nOn the below hand you would be fine to just pass\n\nRule: With 3+ cards in the opponent suit, you can defend, they may be misfitting. (partner might still double for takeout, with 1-2 spades, in which case you can move to the 3 level and be happy to declare. )",
          extra: [
            {
              hands: [{ label: "Example hand where defending is correct, with 3 cards in their suit", hand: { S: "K 10 9", H: "J 3", D: "Q 10 8 2", C: "Q 8 5 4" } }],
            },
          ],
        },
      ],
    },
    {
      id: "cm-3",
      video: "https://youtu.be/Rknta2pV-Gk", // "Compete more - Hand 3" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      // South deals and opens 1C.
      south: { S: "4", H: "A J 4 2", D: "A K 3", C: "10 8 7 4 3" },
      start: ["-", "-", "-", "1C", "1S", "2D", "3S"],
      steps: [
        {
          id: "b1",
          answer: "4D",
          comment:
            "The correct bid of 4D opens up two important principles\n\n1. Always support partner when you have a known 8 card fit, let partner know!\n\n2. You would have liked to support on the 3 level, but are willing to take the push one level higher",
          rule:
            "Always take the push by one level in order to show your support, its better than passing and keeping it a secret. This rule applies to the 3 and 4 level.",
          extra: [
            {
              title: "Lets solidify with an example",
              dealer: "S",
              auction: ["1C", "P", "1H", "2S", "3H"],
              hands: [{ label: "You (South)", hand: { S: "J 3", H: "A 10 8 4", D: "Q 10 3", C: "A J 10 8 3" } }],
              after:
                "You would have gladly bid {{plain|2H}}, but are willing to take the push by one level to {{plain|3H}}.\n\nHowever, in this example we do not have enough \"stuff\" to go onto the 4 level",
            },
            {
              dealer: "S",
              auction: ["1C", "P", "1H", "3S", "P"],
              hands: [{ label: "You (South)", hand: { S: "J 3", H: "A 10 8 4", D: "Q 10 3", C: "A J 10 8 3" } }],
              after:
                "Here we just pass, unfortunately not showing our support, but bidding to the 4 level could be silly without much in partner's hand.\n\nRule: We always are willing to go 1 level higher to show our support, but not 2 levels.",
            },
          ],
        },
      ],
    },
    {
      id: "cm-4",
      video: "https://youtu.be/1ZMYoFFLDBU", // "Compete more - Hand 4" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      article: "supportDouble",
      intro:
        "Note on auction: 2S by partner shows 4 card support, this is true when you are playing support doubles - opener could have doubled with 3 card support. ({{art:supportDouble|Support Doubles in Bridge}})",
      south: { S: "K 10 8 4 3", H: "2", D: "A 9 5 4 2", C: "8 3" },
      start: ["-", "1C", "1H", "1S", "2H", "2S", "3H"],
      steps: [
        {
          id: "b1",
          answer: "3S",
          accept: ["4S"], // Paul, 30 Oct 2026: "accept 4S also"
          comment:
            "Usually we want to leave the 3 level for our opponents. yet here the correct thing is to declare, why?\n\n1. We have a 9 card fit\n\n2. We are distributional, 5-5 shape is great\n\n3. We have a singleton in their suit\n\nRule: Compete to the 3 level with distributional hands and big trump fits. Don't compete with balanced hands and ordinary 8 card trump fits.\n\nMost players never learn when it is correct to compete on the 3 level - it matters a lot when it comes up!\n\n4S is also an okay bid, with only a 7 loser hand, and a 9 card fit ({{art:ltc|Losing Trick Count for Bidding}})\n\nIt is marginal between 3S and 4S, but the main idea is that selling out to 3H is not a good idea, your hand is weighted heavily towards declaring in spades.",
        },
      ],
    },
    {
      id: "cm-5",
      video: "https://youtu.be/TZy2DEEQka4", // "Compete more - Hand 5" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      south: { S: "Q 10 8 4 3", H: "10 8 4 2", D: "7 6 5 4", C: "" },
      start: ["-", "1C", "P"],
      steps: [
        {
          id: "b1",
          answer: "1S",
          comment:
            "You have almost nothing, yet I strongly recommend mustering up a bid on the 1 level when you have a 5 card suit. If you had support for clubs, it would be fine to pass.\n\nYou end up in game if you bid 1S, you are scared for a moment but then you proceed to make it comfortably (if you are a member of Bridge Champions and you know to set up your long suit - clubs).\n\nOn the 1 level you have room to explore, sometimes you will get \"lucky\" and find a fit, and equally as importantly, avoid a misfit.",
          extra: [
            {
              title: "This was the full hand",
              north: { S: "A K J 9", H: "7", D: "A 3", C: "A 9 8 6 5 3" },
            },
          ],
        },
      ],
    },
    {
      id: "cm-6",
      video: "https://youtu.be/m9FkR2O3qfY", // "Compete more - Hand 6" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      vul: "Nil vul",
      south: { S: "K J 10 9 7 6 4 3", H: "5", D: "8 2", C: "7 2" },
      start: ["-", "1C", "2D"],
      steps: [
        {
          id: "b1",
          answer: "4S",
          comment:
            "With 8 cards in a suit, you are entitled to bid up to the 4 level, despite a distinct lack of points.",
          rule:
            "Make yourself heard with an 8 card fit. Weak hands with 8 card fits should go straight to the 4 level. Similar to a 3 level preempt, just 1 level higher and 1 card more.",
        },
        {
          id: "b2",
          answer: "P",
          continuation: ["5D", "P", "P"],
          comment:
            "One of the most potent rules in bridge is\n\n{{em|\"Leave the 5 level for your opponents\"}}\n\nIn general you should either pass or double rather than bidding a suit on the 5 level. Committing to make 11 tricks is a big feat, and will usually end badly.\n\nhere we do not have anything that resembles a double (no defensive tricks), so a simple pass is in order. We have already shown partner our 8 spades, we have nothing more to say.",
        },
      ],
    },
    {
      id: "cm-7",
      video: "https://youtu.be/-sJTb6emz84", // "Compete more - Hand 7" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      intro:
        "The opponents play 2NT as LUBS 5+ - Lowest Unbid suits. This time that is clubs and hearts, showing 5 in each.",
      south: { S: "K 8 3 2", H: "Q 7 4", D: "10 3", C: "Q J 9 2" }, // Paul, 30 Oct 2026: S10 -> 8, HJ -> 7 (small cards)
      start: ["-", "1D", "2N"],
      steps: [
        {
          id: "b1",
          answer: "P",
          prompt:
            "One option a lot of people play is to double, showing some points. In which case either partner can bid something with a distributional hand, or otherwise a lot of the time double the opponents and defend. Do you have enough for that?",
          comment:
            "Again this is the type of hand where you have a combined approximately 20 points, assuming partner has say 12ish. You don't have a special hand at all. The opponents playing on the 3 level is a fine spot for you, often you will profitably defend. It is a bit excessive to double.",
        },
      ],
    },
    {
      id: "cm-8",
      video: "https://youtu.be/etsIrf0fCFo", // "Compete more - Hand 8" on the channel, uploaded 30 Oct 2026 (Sydney) - shown at the end of the hand
      vul: "Nil vul",
      south: { S: "10 4 2", H: "A J 4 2", D: "10 8 4 3", C: "A K" },
      start: ["-", "1H", "4S"],
      steps: [
        {
          id: "b1",
          answer: "X",
          comment:
            "As mentioned we do not want to try to declare a 5 level contract. Double in this context can be a variety of hands, just saying: It is our hand, we can't let the opponents steal it from us.\n\nIts true a 9 card fit is nice (and partner has no reason to suspect you have that), but still follow this rule\n\n{{em|\"Whenever you are thinking about bidding on the 5 level, think twice then don't do it\"}}\n\nYou really need a highly distributional hand to do it, this one is far too balanced.\n\nMost of the time partner will pass, and you will take the contract off. (That is better than bidding on the 5 level and going -1 or -2 yourself!)",
        },
        {
          id: "b2",
          answer: "6H",
          continuation: ["P", "5H", "P"],
          comment:
            "Usually partner will pass, the time they will bid is with a very distributional hand that seems stacked strongly in favour of declaring.",
          extra: [
            {
              hands: [{ label: "Partner (North)", hand: { S: "", H: "K Q 10 9 8 7 6", D: "A 6", C: "Q 7 5 3" } }],
              after: "A void and a 7 card decent suit was the reason partner thought to break the rule of defending rather than venturing to the 5 level.",
            },
          ],
        },
      ],
    },
  ],
};

// 2 over 1: openers rebid (Paul, 4 Nov 2026) - LIVE 4 Nov 2026 ("Go live now"), no videos yet.
// His words VERBATIM. His hands 1 and 4 each hold two hands ("Lets change the hand slightly"), so his 1-7 = tabs 1-9.
// His x's filled with spot cards; fixes listed to him: "yuou" -> "you", "BId" -> "Bid". "ai link article" -> the Part 2
// article; "ai link 2 over 1 article, first one" -> Part 1. **Discuss this with your partner** -> {{em|...}} (coloured).
// South deals every hand; East-West pass throughout. Tabs 1-2 open 1H (Paul, 4 Nov 2026: "1H not 1S opening").
const SOUTH_1S_2C = ["-", "-", "-", "1S", "P", "2C", "P"];
const SOUTH_1H_2C = ["-", "-", "-", "1H", "P", "2C", "P"];
const SOUTH_1S_2H = ["-", "-", "-", "1S", "P", "2H", "P"];
const SOUTH_1S_2D = ["-", "-", "-", "1S", "P", "2D", "P"];
const SOUTH_1H_2D = ["-", "-", "-", "1H", "P", "2D", "P"];
const OPENERS_REBID_21 = {
  id: "openers-rebid",
  title: "2 over 1: openers rebid",
  reading: "twoOverOneRebids",
  hands: [
    {
      id: "or21-1",
      south: { S: "7 3", H: "K Q 8 5 2", D: "A 9 6 4", C: "K 5" },
      start: SOUTH_1H_2C,
      steps: [{ id: "b1", answer: "2D", comment: "If you can bid your 4 card suit on the 2 level simply do it!" }],
    },
    {
      id: "or21-2",
      intro: "Lets change the hand slightly so that we have 4 spades instead of 4 diamonds.",
      south: { S: "A 7 4 3", H: "K Q 8 5 2", D: "9 6", C: "K 5" },
      start: SOUTH_1H_2C,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment:
            "{{em|Discuss this with your partner}}. Some people play this as showing extra values (say 15+). As I mentioned in the article {{art:twoOverOneRebids|2 over 1, rebids - simplified}}, doing this complicates matters unnecessarily. For most partnerships I recommend just bidding your suits at the 2 level freely, without it showing extra values.",
        },
      ],
    },
    {
      id: "or21-3",
      south: { S: "K Q 8 5 2", H: "A 7", D: "Q 6", C: "J 10 4 3" },
      start: SOUTH_1S_2H,
      steps: [
        {
          id: "b1",
          answer: "2N",
          comment:
            'Frequently rebid 2NT, going onto the 3 level with {{plain|3C}} shows a 5 card suit. 2NT is the "catch all" type of bid.\n\n*Note: In terms of frequency, you should rarely be going onto the 3 level with your second bid, and frequently rebidding 2NT.',
        },
      ],
    },
    {
      id: "or21-4",
      south: { S: "K Q 8 5 2", H: "A 7 4", D: "Q 6", C: "J 10 3" },
      start: SOUTH_1S_2H,
      steps: [
        {
          id: "b1",
          answer: "3H",
          comment:
            "Partner's 2H bid shows 5 ({{art:twoOverOne|2 over 1: Game forcing}}). We know we have an 8 card heart fit, let partner know by simply raising.",
          rule: "Always let partner know when we have an 8 card fit.",
        },
      ],
    },
    {
      id: "or21-5",
      south: { S: "K Q 8 5 3 2", H: "A 7", D: "Q 6", C: "J 10 3" },
      start: SOUTH_1S_2H,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment:
            "Rebidding 2S shows 6 cards, nothing fancy. However lets change the hand very slightly by moving one of the clubs to the heart suit, should that change anything?",
        },
      ],
    },
    {
      id: "or21-6",
      south: { S: "K Q 8 5 3 2", H: "A 7 4", D: "Q 6", C: "J 10" },
      start: SOUTH_1S_2H,
      steps: [
        {
          id: "b1",
          answer: "3H",
          comment:
            "Golden rule of bridge is to support partner, let partner know we have an 8 card heart fit. That is more important than showing your 6th spade.",
        },
      ],
    },
    {
      id: "or21-7",
      south: { S: "K Q 8 5 2", H: "7", D: "K 6", C: "A 9 6 4 3" },
      start: SOUTH_1S_2D,
      steps: [{ id: "b1", answer: "3C", comment: "Partner can confidently trust that you have 5C for this bid." }],
    },
    {
      id: "or21-8",
      south: { S: "Q 10 4", H: "K 8 7 5 2", D: "6", C: "A K 9 3" },
      start: SOUTH_1H_2D,
      steps: [
        { id: "b1", answer: "2N", comment: "Once again 2NT appears as the correct bid. Do not bid {{plain|3C}} with a 4 card suit." },
      ],
    },
    {
      id: "or21-9",
      south: { S: "A Q 10 8 5 3 2", H: "K 7", D: "K 6 4", C: "5" },
      start: SOUTH_1S_2C,
      steps: [
        {
          id: "b1",
          answer: "2S",
          comment:
            "Simply 2S, it shows 6+. Remember you are in a game force, you have plenty of time to show your hand and for partner to do the same.",
        },
      ],
    },
  ],
};

// Category labels are Paul's; bid shorthand in them renders as suit symbols.
// `section`: the Guided bidding page is split into Constructive and Competitive bidding (Paul, 14 Oct 2026).
export const SECTIONS = [
  { id: "constructive", label: "Constructive bidding" },
  { id: "competitive", label: "Competitive bidding" },
];
export const CATEGORIES = [
  // guide = the category's general article (Paul, 26 Sep 2026), shown at the top of its pages.
  { id: "1nt", section: "constructive", label: "1NT", guide: "transfers", sets: [RESPONDING_WITH_MAJORS] },
  // Paul, 30 Sep 2026: "MAKE SURE THEY DON'T GO PUBLIC YET, I WANT TO MAKE VIDEOS FIRST" - the 1H/1S set exists only
  // on the local dev copy (localhost), not even for admins on the live site, until he says publish.
  // `group`: categories shown together in one panel on the Guided bidding page, with the practice sets they share
  // underneath (Paul, 7 Oct 2026: "this set applies to both the 1H and 1S and the 1C opening ... make those like a group").
  { id: "1h-1s", section: "constructive", label: "1H and 1S opening", badge: "1H 1S", sets: [MAJOR_OPENINGS], group: "one-level", practice: [MAJOR_PRACTICE_1] }, // published 2 Oct 2026 (Paul)
  { id: "1c-1d", section: "constructive", label: "1C and 1D opening", badge: "1C 1D", sets: [BALANCED_12_14], group: "one-level", practice: [MAJOR_PRACTICE_1] },
  // 2 over 1 (Paul, 9 Oct 2026): published with videos 1-8 ("publish it"); on Game Plan at 2 pts a hand.
  // "2 over 1: openers rebid" PUBLISHED 4 Nov 2026 (Paul: "Go live now"), 2 pts a hand on Game Plan; videos to be added.
  { id: "2-over-1", section: "constructive", label: "2 over 1", badge: "2/1", sets: [TWO_OVER_ONE, OPENERS_REBID_21] },
  // Paul, 15 Oct 2026: released as this month's new Game Plan content ("New content is the new guided bidding").
  { id: "double-or-not", section: "competitive", label: "Double or not", badge: "X", sets: [DOUBLE_OR_NOT] },
  { id: "competing", section: "competitive", label: "Competing", badge: "3D", sets: [COMPETING] },
  // Compete more (Paul, 29 Oct 2026; PUBLISHED 30 Oct 2026 with videos 1-8, 2 pts a hand on Game Plan: "commit this set").
  { id: "compete-more", section: "competitive", label: "Compete more when its right to do so", badge: "3S", sets: [COMPETE_MORE] },
  { id: "2h-2s", section: "constructive", label: "2H and 2S opening", sets: [] },
  { id: "2d-mexican", section: "constructive", label: "2D Mexican opening", sets: [] },
  { id: "overcall-1", section: "competitive", label: "Overcalling on the 1 level", sets: [] },
  { id: "overcall-2", section: "competitive", label: "Overcalling on the 2 level", sets: [] },
];

export function findCategory(id) {
  return CATEGORIES.find((c) => c.id === id) || null;
}

export function findSet(catId, setId) {
  const cat = findCategory(catId);
  return (cat && [...cat.sets, ...(cat.practice || [])].find((s) => s.id === setId)) || null;
}
